<template>
  <CardInteractive
    :title="
      heatmapData?.data?.splitBy && titles[heatmapData.data.splitBy][locale]
        ? titles[heatmapData?.data?.splitBy][locale]
        : title
    "
    :info="info"
    :enableFullscreen="true"
    :isLoading="heatmapData && heatmapData.isLoading && correctFilter"
    :showControls="true"
    :headerDivider="true"
    @toggleFullscreen="fullscreenHeatmap = $event"
  >
    <template #controls>
      <HeatmapPagination
        :totalItems="heatmapData?.data?.axes.x.length ?? 0"
        :itemsPerPage="itemsPerPage"
        :currentPage="currentPage"
        :pageTitle="paginationTitle"
        @stepLeft="stepLeft"
        @stepRight="stepRight"
      />
    </template>
    <HeatmapTable
      v-if="heatmapData?.data?.axes.x.length && correctFilter"
      :fullscreen="fullscreenHeatmap"
      :matrix="heatmapData?.data"
      :color-scale="colorScale"
      :subMeasurementToTitle="subMeasurementToTitle"
      :xSumLabel="xSumLabel?.[locale]"
      :ySumLabel="ySumLabel?.[locale]"
      :tooltipLabel="tooltipLabel[locale]"
      :dataFormat="dataFormat"
      :timeFormat="timeFormat"
      :start="start"
      :end="end"
    />
    <div v-else class="error">
      <TextBase v-if="correctFilter" :text="errorMessages?.NO_DATA[locale]" type="label" />
      <TextBase v-else :text="errorMessages?.INVALID_FILTER[locale]" type="label" />
    </div>
  </CardInteractive>
</template>
<script setup lang="ts">
import { computed, inject, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import HeatmapTable from '@/design-system/molecules/HeatmapTable.vue'
import { ColorScaleInterpolate } from '@/utils/colorScale'
import { FormatOptions } from '../formatter'
import { useDataSource } from '@/composables/useDataSources'
import { HeatmapResponse, HeatmapSplitBy } from '@/api-client/models/indoorclimate/Contracts'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { useI18n } from 'vue-i18n'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'
import HeatmapPagination from '@/design-system/atoms/HeatmapPagination.vue'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { sharedDataKey } from '../../types'
import {
  formatDifferentMonths,
  formatSameMonthMultipleDays,
  formatSameMonthSingleDay
} from '@/utils/calendar-utils/formatters'
import { enUS, sv } from 'date-fns/locale'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { isSameDay, isSameMonth } from 'date-fns'
import { storeToRefs } from 'pinia'

type Titles = {
  [key in HeatmapSplitBy]: LocaleString
}

type Props = {
  title: string
  titles: Titles
  info?: string
  xSumLabel?: LocaleString
  ySumLabel?: LocaleString
  tooltipLabel: LocaleString
  dataFormat: FormatOptions
  timeFormat: FormatOptions
  colorScale: ColorScaleInterpolate
  dataSource: string
  enabledEntityLevels?: EntityLevel[]
  errorMessages?: Record<ErrorState, LocaleString>
  subMeasurementToTitle: Record<string, LocaleString>
  noReverse?: boolean
}

type ErrorState = 'NO_DATA' | 'INVALID_FILTER'

const props = defineProps<Props>()

const { locale } = useI18n()

const { dataSource } = toRefs(props)

const { entityLevel } = storeToRefs(useEntityLevel())

const heatmapData = useDataSource<HeatmapResponse>(dataSource)

const currentPage = ref(0)
const fullscreenHeatmap = ref(false)

const filters = useCurrentFeatureFilters()

const sharedData = inject(sharedDataKey)

const fullscreen = computed(() => sharedData?.fullscreen ?? false)

const correctFilter = computed(
  () =>
    (props.enabledEntityLevels && props.enabledEntityLevels.includes(entityLevel.value)) ||
    !props.enabledEntityLevels
)

const formatRange = (from: Date, to: Date) => {
  if (!isSameMonth(from, to)) {
    return formatDifferentMonths(from, to, { en: enUS, sv: sv }[locale.value])
  }
  if (!isSameDay(from, to)) {
    return formatSameMonthMultipleDays(from, to, { en: enUS, sv: sv }[locale.value])
  }
  return formatSameMonthSingleDay(from, { en: enUS, sv: sv }[locale.value])
}

const paginationTitle = computed(() => {
  const days = heatmapData.value?.data?.axes.x
  if (!days) {
    return ''
  }
  const fromStr = days[start.value]
  const toStr = days[end.value - 1]
  try {
    const from = fromStr ? new Date(fromStr) : undefined
    const to = toStr ? new Date(toStr) : undefined
    if (!from || !to) {
      return ''
    }
    return formatRange(from, to)
  } catch {
    return ''
  }
})

const itemsPerPage = computed(() => {
  const submitted = filters.value?.submitted
  if (!submitted) {
    return 0
  }
  if (fullscreenHeatmap.value || fullscreen.value) {
    return 20
  }
  if ('calendar' in submitted) {
    return 5
  }
  return 4
})

const daysCount = computed(() => heatmapData.value?.data?.axes.x.length ?? 0)

const maxPage = computed(() => Math.ceil(daysCount.value / itemsPerPage.value) - 1)
const rest = computed(() => daysCount.value % itemsPerPage.value)

const start = computed(() =>
  Math.max(
    (currentPage.value !== 0 ? rest.value : 0) +
      (currentPage.value - (rest.value === 0 ? 0 : 1)) * itemsPerPage.value,
    0
  )
)
const end = computed(() =>
  Math.min(
    rest.value + (currentPage.value + (rest.value === 0 ? 1 : 0)) * itemsPerPage.value,
    daysCount.value
  )
)

watch(
  maxPage,
  () => {
    if (!props.noReverse) currentPage.value = maxPage.value
  },
  { immediate: true }
)

function stepLeft() {
  currentPage.value--
}

function stepRight() {
  currentPage.value++
}
</script>
<style lang="scss" scoped>
.error {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
}
</style>
